import {
	FeatureServiceBinder,
	FeatureServiceProviderDefinition,
	SharedFeatureService,
} from '@feature-hub/core';
import { FeatureappServiceV1 } from '@volkswagen-onehub/featureapp-service';
import { LayerManagerV24 } from '@volkswagen-onehub/layer-manager';
import { ServiceConfigProviderV1 } from '@volkswagen-onehub/service-config-provider';
import { UserInteractionServiceV22 } from '@volkswagen-onehub/user-interaction-service';
import axios, { AxiosResponse } from 'axios';
import { formLoad } from './form-loader';
import { OneFormActionsFeatureServiceV1 } from './v1';

export * from './v1';

export interface SharedStarterFeatureService extends SharedFeatureService {
	readonly '1.0.0': FeatureServiceBinder<OneFormActionsFeatureServiceV1>;
}

export type OneFormAction =
	| 'es-oneform--lead-test_drive'
	| 'es-oneform--lead-presupuesto'
	| 'es-oneform--lead-mas_informacion'
	| 'es-oneform--lead-cita'
	| 'es-oneform--lead-stock'
	| 'es-oneform--lead-vo'
	| 'es-oneform--cita-posventa'
	| 'es-oneform--prospect-venta'
	| 'es-oneform--prospect-posventa'
	| 'es-oneform--adhoc-2025-gti50_leads'
	| 'es-oneform--callme';

interface HorariosCallMeNow {
	isOpen?: boolean; // backend could send boolean
	isOpenNow?: number; // fallback numeric flag (1=open, 0=closed)
}

interface EsData {
	urlOrigin: string;
	urlPath: string;
	customConfig: {
		brand: string;
	};
}

//Refactorizar función con getHorariosChatSalesforce en VWData
const getHorariosCallMeNow = async (
	esData: EsData,
): Promise<HorariosCallMeNow | null> => {
	try {
		const response: AxiosResponse<{ horarios: HorariosCallMeNow }> =
			await axios.get(
				`${esData.urlOrigin}${esData.urlPath}/${esData.customConfig.brand}/getHorariosCallMeNow`,
			);

		return response.data.horarios;
	} catch (error) {
		return null;
	}
};

export const oneformActionsFeatureServiceDefinition: FeatureServiceProviderDefinition<SharedStarterFeatureService> =
	{
		id: 'es:oneform-actions-feature-service',

		dependencies: {
			featureServices: {
				'user-interaction-service': '^2.2.0',
				'layer-manager': '^2.5.0',
				'featureapp-service': '^1.0.0',
				'service-config-provider': '^1.0.0',
			},
		},

		create: ({ featureServices }) => {
			const userInteractionService = featureServices[
				'user-interaction-service'
			] as UserInteractionServiceV22;
			const layerManager = featureServices[
				'layer-manager'
			] as LayerManagerV24;
			const featureAppService = featureServices[
				'featureapp-service'
			] as FeatureappServiceV1;
			const serviceConfigProvider = featureServices[
				'service-config-provider'
			] as ServiceConfigProviderV1;

			const esData = serviceConfigProvider.configs['es-vwdata'] as EsData;

			const featureAppLead =
				featureAppService.featureAppConfigurations[
					'es-oneform-lead-embedded'
				];
			const featureAppCitaPosventa =
				featureAppService.featureAppConfigurations[
					'es-oneform-cita-posventa-embedded'
				];
			const featureAppProspect =
				featureAppService.featureAppConfigurations[
					'es-oneform-prospect-embedded'
				];

			const featureAppCallme =
				featureAppService.featureAppConfigurations[
					'es-oneform-callme-embedded'
				];

			userInteractionService.register('es-oneform--lead-test_drive', {
				action: params => {
					formLoad(
						featureAppLead,
						'es-oneform--lead-test_drive',
						params,
						layerManager,
					);
				},
			});

			userInteractionService.register('es-oneform--lead-presupuesto', {
				action: params => {
					formLoad(
						featureAppLead,
						'es-oneform--lead-presupuesto',
						params,
						layerManager,
					);
				},
			});

			userInteractionService.register(
				'es-oneform--lead-mas_informacion',
				{
					action: params => {
						formLoad(
							featureAppLead,
							'es-oneform--lead-mas_informacion',
							params,
							layerManager,
						);
					},
				},
			);

			userInteractionService.register('es-oneform--lead-cita', {
				action: params => {
					formLoad(
						featureAppLead,
						'es-oneform--lead-cita',
						params,
						layerManager,
					);
				},
			});

			userInteractionService.register('es-oneform--lead-stock', {
				action: params => {
					formLoad(
						featureAppLead,
						'es-oneform--lead-stock',
						params,
						layerManager,
					);
				},
			});

			userInteractionService.register('es-oneform--lead-vo', {
				action: params => {
					formLoad(
						featureAppLead,
						'es-oneform--lead-vo',
						params,
						layerManager,
					);
				},
			});

			userInteractionService.register('es-oneform--cita-posventa', {
				action: params => {
					formLoad(
						featureAppCitaPosventa,
						'es-oneform--cita-posventa',
						params,
						layerManager,
					);
				},
			});

			userInteractionService.register('es-oneform--prospect-venta', {
				action: params => {
					formLoad(
						featureAppProspect,
						'es-oneform--prospect-venta',
						params,
						layerManager,
					);
				},
			});

			userInteractionService.register('es-oneform--prospect-posventa', {
				action: params => {
					formLoad(
						featureAppProspect,
						'es-oneform--prospect-posventa',
						params,
						layerManager,
					);
				},
			});

			// Solo registramos la action para callmenow si está abierto.
			getHorariosCallMeNow(esData)
				.then(horariosCallMeNow => {
					const isOpen =
						horariosCallMeNow?.isOpen ??
						horariosCallMeNow?.isOpenNow === 1;
					if (isOpen) {
						userInteractionService.register('es-oneform--callme', {
							action: params => {
								formLoad(
									featureAppCallme,
									'es-oneform--callme',
									params,
									layerManager,
								);
							},
						});
					}
				})
				.catch(() => {});

			return {
				'1.0.0': () => ({
					featureService: {
						helloWorld: () => 'Hello world',
					},
				}),
			};
		},
	};
